import { useLayoutEffect, useRef, useState, type ReactNode } from "react";

export const TAB_TITLE_FIT_CLASS = "font-semibold tracking-normal";
export const TAB_TITLE_BASE_FONT_PX = 36;
export const TAB_TITLE_CLASS = `text-4xl ${TAB_TITLE_FIT_CLASS}`;

const MIN_SCALE = 0.55;
const MIN_SCALE_FLOOR = 0.45;

type TabPageTitleProps = {
  title: string;
  suffix?: ReactNode;
};

export function TabPageTitle({ title, suffix }: TabPageTitleProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLHeadingElement>(null);
  const [fontSizePx, setFontSizePx] = useState(TAB_TITLE_BASE_FONT_PX);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return;

    const fit = () => {
      const available = container.clientWidth;
      if (available <= 0) return;

      const suffixWidth = suffix ? 36 : 0;
      const titleBudget = Math.max(0, available - suffixWidth - (suffix ? 4 : 0));

      const previousSize = content.style.fontSize;
      content.style.fontSize = `${TAB_TITLE_BASE_FONT_PX}px`;
      const naturalWidth = content.scrollWidth;
      content.style.fontSize = previousSize;

      const fitScale = (titleBudget - 2) / naturalWidth;
      const nextScale =
        naturalWidth > titleBudget - 2 ? Math.max(MIN_SCALE, fitScale) : 1;
      const resolvedScale =
        naturalWidth * nextScale > titleBudget - 2
          ? Math.max(MIN_SCALE_FLOOR, fitScale)
          : nextScale;
      setFontSizePx(TAB_TITLE_BASE_FONT_PX * resolvedScale);
    };

    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(container);
    return () => observer.disconnect();
  }, [title, suffix]);

  return (
    <div ref={containerRef} className="flex min-w-0 items-center gap-1">
      <h1
        ref={contentRef}
        className={`min-w-0 whitespace-nowrap ${TAB_TITLE_FIT_CLASS}`}
        style={{ fontSize: fontSizePx, lineHeight: 1.1 }}
      >
        {title}
      </h1>
      {suffix ? <div className="shrink-0">{suffix}</div> : null}
    </div>
  );
}
